import { Form, Input, Modal } from "antd";
import { AppButton } from "components/button/AppButton";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useAddPermission } from "../hooks/useAddPermission";
import {
  AuthorizationGuard,
  CreatePermissionRequest,
} from "../types";
import { useQueryClient } from "react-query";
import { QUERY_KEY_FOR_PERMISSIONS } from "../hooks/useGetPermissions";
import { getAuthorizationErrorMessage } from "../authorizationError";

interface AddPermissionProps {
  open: boolean;
  guardName: AuthorizationGuard;
  handleClose: () => void;
}

interface AddPermissionFormValues {
  name: string;
}

export const AddPermission = ({
  open,
  guardName,
  handleClose,
}: AddPermissionProps) => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useAddPermission();

  const handleFormSubmit = (values: AddPermissionFormValues) => {
    const props: CreatePermissionRequest = {
      token,
      name: values.name.trim(),
      guard_name: guardName,
    };
    mutate(props, {
      onError: (error) => {
        openNotification({
          title: "Error",
          state: "error",
          description: getAuthorizationErrorMessage(
            error,
            "Could not create the permission"
          ),
          duration: 6.0,
        });
      },
      onSuccess: () => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Added Successfully",
          duration: 4.5,
        });
        form.resetFields();
        queryClient.invalidateQueries([QUERY_KEY_FOR_PERMISSIONS, guardName]);
        handleClose();
      },
    });
  };

  return (
    <Modal
      style={{ top: 20 }}
      footer={false}
      open={open}
      onCancel={handleClose}
      title={`Add new ${guardName === "admins" ? "Admin" : "User"} Permission`}
      destroyOnHidden
    >
      <Form layout="vertical" requiredMark="optional" className="mt-5"
        onFinish={handleFormSubmit}
        form={form}
      >
        <Form.Item
          name="name"
          label="Permission Name"
          rules={generalValidationRules}
          extra="Must match the exact name the application checks for, e.g. Manage Jobs"
        >
          <Input placeholder="Please enter permission name" />
        </Form.Item>

        <AppButton label="Submit" type="submit" isLoading={isLoading} />
      </Form>
    </Modal>
  );
};
